export const roleManagementRoutes = {
  admin: '/yonetim/admin',
  editor: '/yonetim/editor',
  il_yoneticisi: '/yonetim/il-yoneticisi',
  lig_yoneticisi: '/yonetim/lig-yoneticisi',
  mac_yoneticisi: '/yonetim/mac-yoneticisi',
  // Disiplin Kurulu Üyesi yalnızca ceza dosyalarıyla ilgilenir; girişten sonra
  // doğrudan ceza ekranına düşer. Sosyal medya yöneticisi haber merkezine.
  disiplin_kurulu: '/editor/cezalar',
  sosyal_medya_yoneticisi: '/editor/haberler',
  uye: '/panel',
  oyuncu: '/panel',
  takim_baskani: '/panel',
  double: '/panel',
};

// Üye rolleri, üyenin seçtiği profil tipini yansıtır:
//   uye | oyuncu | takim_baskani | double
// "double" hem oyuncu hem takım başkanı kapsamını açar.
export const TEAM_ROLES = ['takim_baskani', 'double'];
export const PLAYER_ROLES = ['oyuncu', 'double'];
export const MEMBER_ROLES = ['uye', 'oyuncu', 'takim_baskani', 'double'];

export const MANAGEMENT_ROLES = [
  'admin',
  'editor',
  'il_yoneticisi',
  'lig_yoneticisi',
  'mac_yoneticisi',
  'disiplin_kurulu',
  'sosyal_medya_yoneticisi',
];

// İl kısıtı olmayan roller; diğer tüm yönetim rolleri yalnızca kendi ilinde
// çalışır (sunucuda config/roles.js NATIONWIDE_ROLES ile aynı liste).
export const NATIONWIDE_ROLES = ['admin', 'disiplin_kurulu'];
export const isNationwideRole = (role) => NATIONWIDE_ROLES.includes(role);
// Oturumdaki kullanıcının ŞU ANKİ kapsamı Türkiye geneli mi? Türkiye geneli
// rol üst çubuktan bir il seçtiğinde (utils/actingCity.js) o ilin yetkilisi
// gibi çalışır ve il kısıtlı sayılır.
export const isNationwideUser = (user) => isNationwideRole(user?.role) && !user?.actingCity;

// Rol adları ekranlarda tek tek yazılıyordu ve listeler birbirini tutmuyordu:
// bazı ekranlarda disiplin_kurulu ile sosyal_medya_yoneticisi hiç yoktu, bazı
// yerlerde ham anahtar ("il_yoneticisi") görünüyordu. Etiketin tek kaynağı
// burasıdır (sunucuda constants/permissions.js ROLE_LABELS ile aynı).
export const ROLE_LABELS = {
  admin: 'Yönetici (Admin)',
  editor: 'Editör',
  il_yoneticisi: 'İl Yöneticisi',
  lig_yoneticisi: 'Lig Yöneticisi',
  mac_yoneticisi: 'Maç Yöneticisi',
  disiplin_kurulu: 'Disiplin Kurulu Üyesi',
  sosyal_medya_yoneticisi: 'Sosyal Medya Yöneticisi',
  uye: 'Üye',
  oyuncu: 'Oyuncu',
  takim_baskani: 'Takım Başkanı',
  double: 'Oyuncu + Takım Başkanı',
};

export const roleLabel = (role) => ROLE_LABELS[role] || role || '';

// Takım iç notları (takım rehberi / saha planlama) ve takımın iletişim bilgisi.
// Erişim artık yetki matrisindedir (teams.notes); liste varsayılanı belgeler.
export const TEAM_NOTE_ROLES = ['admin', 'editor', 'il_yoneticisi'];
export const canManageTeamNotes = (user) => hasPermission(user, 'teams.notes');

// Muhasebe sayfası.
// Erişim artık yetki matrisindedir (accounting.view / accounting.manage) ve
// her role verilebilir; liste yalnızca varsayılanı belgeler.
export const ACCOUNTING_ROLES = ['admin', 'il_yoneticisi'];
export const ACCOUNTING_PERMISSIONS = ['accounting.view', 'accounting.manage'];
// Türkiye Geneli Muhasebe (illerden alınan maç başı ücretler): varsayılan
// olarak yalnızca admin; panelden Türkiye geneli bir role verilir.
export const NATIONAL_ACCOUNTING_PERMISSIONS = ['national_accounting.view', 'national_accounting.manage'];
export const canAccessAccounting = (user) => hasAnyPermission(user, ACCOUNTING_PERMISSIONS);

export const hasTeamRole = (role) => TEAM_ROLES.includes(role);
export const hasPlayerRole = (role) => PLAYER_ROLES.includes(role);
export const isManagementRole = (role) => MANAGEMENT_ROLES.includes(role);

export const getManagementRouteForRole = (role) => {
  return roleManagementRoutes[role] || (isManagementRole(role) ? '/editor' : '/');
};

/* ---------------------------------------------------------------------------
   Panelden yönetilen roller
   ---------------------------------------------------------------------------
   Roller artık Kullanıcı Yetkilendirme ekranından eklenebiliyor; adları,
   kapsamları (il / Türkiye geneli) ve giriş sayfaları değiştirilebiliyor.
   Sunucu oturum yanıtında rol kataloğunu gönderir (user.roleCatalog ve
   user.roleInfo). Bu fonksiyon yukarıdaki listeleri YERİNDE günceller; böylece
   MANAGEMENT_ROLES, isNationwideRole, roleLabel ve getManagementRouteForRole
   kullanan her ekran yeni rolleri tanır. */
export const BUILTIN_MANAGEMENT_ROLES = Object.freeze([...MANAGEMENT_ROLES]);

const replaceList = (target, values) => target.splice(0, target.length, ...values);

export function registerRoleCatalog(catalog) {
  if (!Array.isArray(catalog) || !catalog.length) return;
  const management = catalog.filter((role) => role?.isManagement && role.key);
  if (!management.length) return;
  replaceList(MANAGEMENT_ROLES, management.map((role) => role.key));
  replaceList(NATIONWIDE_ROLES, management.filter((role) => role.nationwide).map((role) => role.key));
  catalog.forEach((role) => {
    if (!role?.key) return;
    if (role.label) ROLE_LABELS[role.key] = role.label;
    if (role.isManagement && role.homePath) roleManagementRoutes[role.key] = role.homePath;
  });
}

/** Oturumdaki kullanıcının rol bilgisini (ve yönetim hesabıysa kataloğu) kaydeder. */
export function registerUserRoles(user) {
  if (!user) return;
  registerRoleCatalog(user.roleCatalog);
  const info = user.roleInfo;
  if (!info?.key) return;
  if (info.label) ROLE_LABELS[info.key] = info.label;
  if (info.isManagement) {
    if (!MANAGEMENT_ROLES.includes(info.key)) MANAGEMENT_ROLES.push(info.key);
    if (info.homePath) roleManagementRoutes[info.key] = info.homePath;
    const index = NATIONWIDE_ROLES.indexOf(info.key);
    if (info.nationwide && index < 0) NATIONWIDE_ROLES.push(info.key);
    if (!info.nationwide && index >= 0) NATIONWIDE_ROLES.splice(index, 1);
  }
}

/**
 * Kullanıcının panelden verilmiş yetkisi var mı?
 *
 * Yetkiler oturum yanıtında (user.permissions) gelir ve admin panelindeki
 * "Kullanıcı Yetkilendirme" ekranından role göre düzenlenir. Admin her zaman
 * tüm yetkilere sahiptir. Eski bir oturum önbelleğinde permissions alanı
 * bulunmayabilir; bu durumda menüler boş kalmasın diye rolün yönetim rolü
 * olması yeterli sayılır — asıl kontrol her hâlükârda sunucudadır.
 */
export const hasPermission = (user, permission) => {
  if (!user || !permission) return false;
  if (user.role === 'admin') return true;
  if (!Array.isArray(user.permissions)) return isManagementRole(user.role) || Boolean(user.isManagement);
  return user.permissions.includes(permission);
};

export const hasAnyPermission = (user, permissions = []) =>
  permissions.some((permission) => hasPermission(user, permission));
